import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, TextField, Typography } from "@mui/material";
import modalStyle from "../../styles/modalStyle";
import CloseIcon from '@mui/icons-material/Close';
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import Pincode from "../../interfaces/pincode";
import pincodeColumnDefs from "../../colDef/pincodeColumnDef";
import { GridPaginationModel } from "@mui/x-data-grid";
import apiClient from "../../axiosClient";
import { commaSeparatedPincodePattern, decimalNumberPattern } from "../../constants/patterns";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import DropDown from "../baseComponents/dropdown";
import { debounce } from "../../utils/commonUtils";
import DropDownValue from "../baseComponents/dropdownvalue";
import UploadComponent from "../baseComponents/uploadComponent";
import { pincodeUploadFormat } from "../../constants/columnFormats";

const ManagePincodes: React.FC = () => {
    const districtState = useSelector((state: RootState) => state.districtsAndStates);
    const states = districtState.states;
    const districts = districtState.districts;
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [pincodeData, setPincodeData] = useState<Pincode[]>([]);
    const [pincodeDataCount, setPincodeDataCount] = useState<number>(0);
    const [filteredDistricts, setFilteredDistricts] = useState<string[]>(districts.map(val => val.district));
    const [pincode, setPincode] = useState<string>('');
    const [area, setArea] = useState<string>('');
    const [district, setDistrict] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [latitude, setLatitude] = useState<string>('');
    const [longitude, setLongitude] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
    const [openPincodeUploadModal, setOpenPincodeUploadModal] = useState<boolean>(false);

    const filterOptions = (options: any[], { inputValue }: { inputValue: string }, valueProp: string | undefined) => {
        const lower = inputValue.trim().toLowerCase();
        if (lower.length < 2) return [];         // require >= 2 chars
        const filtered = options.filter(opt => valueProp ? opt[valueProp].toLowerCase().includes(lower) : opt.toLowerCase().includes(lower));
        filtered.sort((a, b) => {
            const aStarts = valueProp ? a[valueProp].toLowerCase().startsWith(lower) : a.toLowerCase().startsWith(lower);
            const bStarts = valueProp ? b[valueProp].toLowerCase().startsWith(lower) : b.toLowerCase().startsWith(lower);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return valueProp ? a[valueProp].localeCompare(b) : a.localeCompare(b);
        });
        return filtered;
    };

    const fetchPincodes = async (page: number) => {
        try {
            setLoading(true);
            let apiUrl = `/api/v1/pin_code?row_index=${page}`;
            if (searchQuery) {
                apiUrl += `&search_value=${searchQuery}`;
            }
            const response = await apiClient.get(apiUrl);
            if (response.status === 200) {
                setPincodeData(response.data.pin_codes);
                setPincodeDataCount(parseInt(response.data.count));
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const createPincode = async () => {
        try {
            if (!latitude || !longitude || !area || !state || !district || !pincode) {
                alert('Please enter all fields');
                return;
            }
            if (!decimalNumberPattern.test(latitude) || !decimalNumberPattern.test(longitude)) {
                alert('Please enter coordinates properly');
                return;
            }
            if (!commaSeparatedPincodePattern.test(pincode)) {
                alert('Please enter pincodes properly');
            }
            const body = {
                latitude,
                longitude,
                area_name: area,
                district,
                state_name: state,
                pin_code: pincode
            };
            const response = await apiClient.post(`/api/v1/pin_code`, [body]);
            if (response.status === 200) {
                setPaginationModel({ page: 0, pageSize: 100 });
                alert('Success! Pincode added successfully');
                await fetchPincodes(0);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    const debouncedSearch = useMemo(() => debounce(fetchPincodes, 500), [paginationModel, searchQuery]);

    useEffect(() => {
        debouncedSearch.cancel();
        debouncedSearch(paginationModel.page);
        return () => {
            debouncedSearch.cancel();
        };
    }, [paginationModel, searchQuery]);

    useEffect(() => {
        if (state) {
            setFilteredDistricts(districts.filter(val => val.state === state).map(val => val.district));
        } else {
            setFilteredDistricts(districts.map(val => val.district));
        }
    }, [state]);

    const handleCloseModal = () => {
        setPincode('');
        setArea('');
        setDistrict('');
        setState('');
        setLatitude('');
        setLongitude('');
        setOpenModal(false);
    };

    return (
        <Box m={2} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <TextField
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.currentTarget.value)}
                    placeholder="Search..."
                    variant="outlined"
                    slotProps={{ input: { sx: {
                        fontSize: "14px",
                        padding: "0px",
                        margin: "8px",
                        minWidth: "600px",
                        maxHeight: "36px"
                    }}}}
                />
                <Box display="flex" justifyContent="flex-end" marginY="10px">
                    <Button sx={{ mr: 2 }} variant="outlined" onClick={() => setOpenPincodeUploadModal(true)}>
                        <Typography variant="subtitle2">Upload Pincodes</Typography>
                    </Button>
                    <Button variant="outlined" onClick={() => setOpenModal(true)}>
                        <Typography variant="subtitle2">Add New Pincode</Typography>
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <SearchResultGrid
                    height='80vh'
                    getRowId={(row: Pincode) => row.pin_code}
                    results={pincodeData}
                    onSelectRow={() => {}}
                    columns={pincodeColumnDefs}
                    rowCount={pincodeDataCount}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    showToolBar={false}
                />
            )}

            <Modal open={openModal} onClose={handleCloseModal}>
                <Box sx={modalStyle}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography id="modal-title" variant="h6">
                        Add New Pincode
                        </Typography>
                        <IconButton onClick={handleCloseModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <TextField
                        label="Enter Pin Code"
                        name="pin code"
                        value={pincode}
                        onChange={(event) => setPincode(event?.target.value.slice(0, 6))}
                        fullWidth
                        required
                        margin="normal"
                    />
                    <TextField
                        label="Enter Area Name"
                        name="area name"
                        value={area}
                        onChange={(event) => setArea(event?.target.value)}
                        fullWidth
                        required
                        margin="normal"
                    />
                    <DropDownValue 
                        label="Select District" 
                        items={filteredDistricts} 
                        selectedValue={district} multiple={false}
                        onSelection={value => setDistrict(value)}
                        filterOptions={filterOptions} 
                    />
                    <DropDown
                        label="Select State"
                        selectedValue={state}
                        onSelection={value => setState(value)}
                        items={states}
                        required
                    />
                    <TextField
                        label="Enter Latitude"
                        name="latitude"
                        value={latitude}
                        onChange={(event) => setLatitude(event?.target.value)}
                        fullWidth
                        required
                        margin="normal"
                    />
                    <TextField
                        label="Enter Longitude"
                        name="longitude"
                        value={longitude}
                        onChange={(event) => setLongitude(event?.target.value)}
                        fullWidth
                        required
                        margin="normal"
                    />
                    <Box mt={2} textAlign="right">
                        <Button variant="contained" color="primary" onClick={createPincode}>
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <UploadComponent
                label="Upload Pincodes"
                uploadApi="/api/v1/upload_pincode"
                uploadType="Pincode"
                open={openPincodeUploadModal}
                onClose={() => setOpenPincodeUploadModal(false)}
                uploadFormat={pincodeUploadFormat}
            />
        </Box>
    );
};

export default ManagePincodes;